import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import Stepper from '@/v2/components/Stepper';
import {
  useCartState,
  useCartDispatch,
} from '../../../components/context/CartContext/CartContext';
import useFetch from 'use-http';
import { useCartData } from '../CartPage/context/CartDataContext';
import { useParams } from 'react-router-dom';
import { ComplexPriceStep } from './Components/ComplexPriceStep';
import ComplexPatientInfoStep from './Steps/ComplexPatientInfoStep';
import { useUserState } from '@/components/context/UserContext';
import personalCodeValidation from '@/components/hooks/personalCodeValidation';
import SuccessStep from '../CartPage/Steps/SuccessStep';
import ErrorStep from '../CartPage/Steps/ErrorStep';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import SuccessStepComplex from './Steps/SuccessStepComplex';
import RegistrationErrorDialog from '../CartPage/Components/RegistrationErrorDialog';
import {
  buildComplexExponeaPayload,
  buildComplexTrackingData,
  clearExponeaBookingPayload,
  persistBookingTrackingData,
  persistExponeaBookingPayload,
  pushBeginCheckoutTrackingData,
  pushExponeaBookingItemEvent,
  pushExponeaBookingResultEvent,
  readExponeaBookingPayload,
} from '@/v2/utils/bookingTracking';

const STEBBY_REFERENCE_KEY = 'stebbyReference';
const STEBBY_COMPLEX_REGISTRATION_INPUT_KEY = 'stebbyComplexRegistrationInput';

const PAYMENT_TYPES = {
  EBank: 'EBank',
  Stebby: 'Stebby',
};

const BACKEND_PAYMENT_TYPES = {
  Paysera: 'Paysera',
  Stebby: 'Stebby',
};

const isProduction = process.env.REACT_APP_IS_PRODUCTION === 'true';

const GENERIC_ERROR_MSG =
  'Atsiprašome, tačiau jūsų registracija nepavyko dėl nenumatytos klaidos. Bandykite dar kartą.';

const ComplexCartPage = () => {
  const { complex, loading, setLoading, complexPrice } = useCartData();
  const dispatch = useCartDispatch();
  const cartState = useCartState();
  const userState = useUserState();
  const history = useHistory();
  const { step } = useParams();

  const stebbyCallbackHandledRef = useRef(false);
  const checkoutTrackedRef = useRef(null);

  const { request: registrationRequest, response: registrationResponse } =
    useFetch('/Registrations/ComplexServiceRegistration');

  const {
    request: simpleRegistrationRequest,
    response: simpleRegistrationResponse,
  } = useFetch('/Registrations/SimpleComplexRegistration');

  const {
    post: postStebby,
    get: getStebby,
    response: stebbyResponse,
    loading: isStebbyLoading,
  } = useFetch('/stebby');

  const [stepIndex, setStepIndex] = useState(0);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [paymentType, setPaymentType] = useState(PAYMENT_TYPES.EBank);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] =
    useState(GENERIC_ERROR_MSG);

  const [, , , birthDateFromPersonalCode] = personalCodeValidation();

  const stepTitles = useMemo(
    () => ['Padalinys', 'Mokėtojo duomenys', 'Apmokėjimas'],
    [],
  );

  const isSuccessRoute = step === 'success';
  const isErrorRoute = step === 'error';
  const isCancelRoute = step === 'cancel';
  const isStebbySuccessRoute = step === 'stebby-success';
  const isStebbyCancelRoute = step === 'stebby-cancel';

  const clearStebbySession = useCallback(() => {
    sessionStorage.removeItem(STEBBY_REFERENCE_KEY);
    sessionStorage.removeItem(STEBBY_COMPLEX_REGISTRATION_INPUT_KEY);
  }, []);

  const nextStep = useCallback(() => {
    setStepIndex((prev) => Math.min(prev + 1, stepTitles.length - 1));
  }, [stepTitles.length]);

  const prevStep = useCallback(() => {
    setStepIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  const buildPayload = useCallback(
    (formData, selectedPaymentType) => {
      const isStebby = selectedPaymentType === PAYMENT_TYPES.Stebby;
      const backendPaymentType = isStebby
        ? BACKEND_PAYMENT_TYPES.Stebby
        : BACKEND_PAYMENT_TYPES.Paysera;

      return {
        patientEmail: formData.payerEmail,
        patientName: formData.payerFirstName,
        patientPhoneNumber: formData.payerPhone,
        patientSurname: formData.payerLastName,
        patientIdNumber: userState.isLoggedIn
          ? userState.personalCode
          : formData.payerPersonalCode,
        amount: complexPrice.price,
        complexServiceId: complex.service.serviceId,
        departmentId: complexPrice.departmentId,
        price: complexPrice.price,
        giftCouponNumbers: complexPrice.appliedCoupons ?? [],
        discountCode: complexPrice.discountCode,
        priceId: complexPrice.priceId,
        childIdNumber: formData.patientPersonalCode,
        childName: formData.patientFirstName,
        childSurname: formData.patientLastName,
        childEspbiEsi: formData.selectedChildEspbiEsi,
        childBirthDate: formData.patientPersonalCode
          ? birthDateFromPersonalCode(formData.patientPersonalCode)
          : '',
        PaymentType: backendPaymentType,
        paymentType: backendPaymentType,
        frontUrls: {
          cancelFrontURL: isStebby
            ? '/kompleksines-paslaugos/cart/stebby-cancel'
            : '/payment/status/cancel',
          acceptFrontUrl: isStebby
            ? '/kompleksines-paslaugos/cart/stebby-success'
            : '/kompleksines-paslaugos/cart/success',
          errorFrontUrl: '/kompleksines-paslaugos/cart/error',
        },
      };
    },
    [
      birthDateFromPersonalCode,
      complex,
      complexPrice,
      userState.isLoggedIn,
      userState.personalCode,
    ],
  );

  const buildTracking = useCallback(
    (formData, orderId) => ({
      trackingData: buildComplexTrackingData({
        complex,
        complexPrice,
        orderId,
      }),
      exponeaPayload: buildComplexExponeaPayload({
        complex,
        complexPrice,
        formData,
        orderId,
      }),
    }),
    [complex, complexPrice],
  );

  const submitSimpleComplexRegistration = useCallback(
    async ({ payload, formData, trackingData, exponeaPayload }) => {
      setFormSubmitting(true);
      setLoading(true);

      try {
        const responseData = await simpleRegistrationRequest.post('', payload);

        const orderId =
          responseData?.couponOrderId ||
          responseData?.complexServiceRegistrationId ||
          responseData?.orderId;

        const finalTrackingData = buildTracking(formData, orderId).trackingData;
        const finalExponeaPayload =
          exponeaPayload || buildTracking(formData, orderId).exponeaPayload;

        if (simpleRegistrationResponse.status > 299) {
          pushExponeaBookingResultEvent(finalExponeaPayload, 'Failed');
          setErrorDialogMessage(GENERIC_ERROR_MSG);
          setErrorDialogOpen(true);
          return false;
        }

        persistBookingTrackingData(finalTrackingData);
        persistExponeaBookingPayload(finalExponeaPayload);
        pushExponeaBookingItemEvent(finalExponeaPayload);

        history.push('/kompleksines-paslaugos/cart/success');
        return true;
      } finally {
        setFormSubmitting(false);
        setLoading(false);
      }
    },
    [
      buildTracking,
      history,
      setLoading,
      simpleRegistrationRequest,
      simpleRegistrationResponse.status,
    ],
  );

  const submitComplexRegistration = useCallback(
    async ({ payload, formData, trackingData, exponeaPayload }) => {
      setFormSubmitting(true);
      setLoading(true);

      try {
        const responseData = await registrationRequest.post('', payload);

        const orderId =
          responseData?.couponOrderId ||
          responseData?.complexServiceRegistrationId ||
          responseData?.orderId;

        const finalTrackingData = buildTracking(formData, orderId).trackingData;
        const finalExponeaPayload =
          exponeaPayload || buildTracking(formData, orderId).exponeaPayload;

        if (registrationResponse.status >= 500) {
          pushExponeaBookingResultEvent(finalExponeaPayload, 'Failed');
          setErrorDialogMessage(responseData?.message || GENERIC_ERROR_MSG);
          setErrorDialogOpen(true);
          return false;
        }

        if (registrationResponse.status >= 400) {
          pushExponeaBookingResultEvent(finalExponeaPayload, 'Failed');
          setErrorDialogMessage(responseData?.message || GENERIC_ERROR_MSG);
          setErrorDialogOpen(true);
          return false;
        }

        persistBookingTrackingData(finalTrackingData);
        persistExponeaBookingPayload(finalExponeaPayload);
        pushExponeaBookingItemEvent(finalExponeaPayload);

        const payseraUrl =
          responseData?.paymentUrl ||
          responseData?.payseraUrl ||
          responseData?.PayseraUrl;

        if (!payseraUrl && orderId) {
          history.push('/kompleksines-paslaugos/cart/success');
          return true;
        }

        dispatch({
          type: 'SET_REGISTRATION_ID',
          registrationId: orderId,
          orderType: 'complex',
        });

        window.location.href = payseraUrl;
        return true;
      } finally {
        setFormSubmitting(false);
        setLoading(false);
      }
    },
    [
      buildTracking,
      dispatch,
      history,
      registrationRequest,
      registrationResponse.status,
      setLoading,
    ],
  );

  const submitRegistrationByWorkflow = useCallback(
    async ({ payload, formData, trackingData, exponeaPayload }) => {
      if (complex?.simpleWorkflow) {
        return submitSimpleComplexRegistration({
          payload,
          formData,
          trackingData,
          exponeaPayload,
        });
      }

      return submitComplexRegistration({
        payload,
        formData,
        trackingData,
        exponeaPayload,
      });
    },
    [
      complex?.simpleWorkflow,
      submitComplexRegistration,
      submitSimpleComplexRegistration,
    ],
  );

  const startStebbyAuthorization = useCallback(
    async ({ payload, formData, trackingData, exponeaPayload }) => {
      try {
        const result = await postStebby('/identify/request-token?option=complex');

        if (!stebbyResponse.ok || !result?.redirectUrl || !result?.reference) {
          console.error('❌ Failed to start Stebby authorization:', result);
          setErrorDialogMessage(GENERIC_ERROR_MSG);
          setErrorDialogOpen(true);
          return;
        }

        sessionStorage.setItem(STEBBY_REFERENCE_KEY, result.reference);
        sessionStorage.setItem(
          STEBBY_COMPLEX_REGISTRATION_INPUT_KEY,
          JSON.stringify({
            payload,
            formData,
            trackingData,
            exponeaPayload,
          }),
        );

        window.location.href = result.redirectUrl;
      } catch (err) {
        console.error('❌ Stebby authorization error:', err);
        setErrorDialogMessage(GENERIC_ERROR_MSG);
        setErrorDialogOpen(true);
      }
    },
    [postStebby, stebbyResponse.ok],
  );

  const showPaymentOptions = useCallback(
    async (formData) => {
      if (!formData) {
        console.error('❌ Complex form data is empty');
        setErrorDialogMessage(GENERIC_ERROR_MSG);
        setErrorDialogOpen(true);
        return;
      }

      const canUseStebby = !isProduction && !complex?.simpleWorkflow;
      const selectedPaymentType =
        canUseStebby && paymentType === PAYMENT_TYPES.Stebby
          ? PAYMENT_TYPES.Stebby
          : PAYMENT_TYPES.EBank;

      const payload = buildPayload(formData, selectedPaymentType);
      const orderId = payload.complexServiceId;
      const { trackingData, exponeaPayload } = buildTracking(formData, orderId);

      try {
        if (selectedPaymentType === PAYMENT_TYPES.Stebby) {
          await startStebbyAuthorization({
            payload,
            formData,
            trackingData,
            exponeaPayload,
          });
          return;
        }

        await submitRegistrationByWorkflow({
          payload,
          formData,
          trackingData,
          exponeaPayload,
        });
      } catch (err) {
        console.error('❌ Complex registration error:', err);
        setErrorDialogMessage(GENERIC_ERROR_MSG);
        setErrorDialogOpen(true);
      }
    },
    [
      buildPayload,
      buildTracking,
      complex?.simpleWorkflow,
      paymentType,
      startStebbyAuthorization,
      submitRegistrationByWorkflow,
    ],
  );

  useEffect(() => {
    const isPaymentCallbackRoute =
      isSuccessRoute ||
      isErrorRoute ||
      isCancelRoute ||
      isStebbySuccessRoute ||
      isStebbyCancelRoute;

    if (complex == null && !isPaymentCallbackRoute) {
      if (cartState.registrationId) {
        history.push('/payment/status/cancel');
      } else {
        history.push('/kompleksines-paslaugos');
      }
    }
  }, [
    cartState.registrationId,
    complex,
    history,
    isCancelRoute,
    isErrorRoute,
    isStebbyCancelRoute,
    isStebbySuccessRoute,
    isSuccessRoute,
  ]);

  useEffect(() => {
    if (isSuccessRoute) {
      dispatch({ type: 'RESET_REGISTRATION_ID' });

      if (userState.isLoggedIn) {
        history.push('/userAccount/complex-services');
        return;
      }

      setStepIndex(stepTitles.length);
      return;
    }

    if (isErrorRoute || isCancelRoute) {
      const exponeaPayload = readExponeaBookingPayload();
      pushExponeaBookingResultEvent(exponeaPayload, 'Cancelled');
      clearExponeaBookingPayload();
      setStepIndex(-1);
      return;
    }

    if (isStebbyCancelRoute) {
      const exponeaPayload = readExponeaBookingPayload();
      pushExponeaBookingResultEvent(exponeaPayload, 'Cancelled');
      clearExponeaBookingPayload();
      clearStebbySession();
      setStepIndex(-1);
    }
  }, [
    clearStebbySession,
    dispatch,
    history,
    isCancelRoute,
    isErrorRoute,
    isStebbyCancelRoute,
    isSuccessRoute,
    stepTitles.length,
    userState.isLoggedIn,
  ]);

  useEffect(() => {
    if (!complex || !complexPrice) return;
    const itemId = complex.service?.serviceId || complex.serviceId || complex.id;
    if (checkoutTrackedRef.current === itemId) return;
    checkoutTrackedRef.current = itemId;

    pushBeginCheckoutTrackingData(
      buildComplexTrackingData({
        complex,
        complexPrice,
      }),
    );
  }, [complex, complexPrice]);

  useEffect(() => {
    if (!isStebbySuccessRoute || stebbyCallbackHandledRef.current) return;

    stebbyCallbackHandledRef.current = true;

    const completeStebbyFlow = async () => {
      try {
        const reference = sessionStorage.getItem(STEBBY_REFERENCE_KEY);
        const registrationInputRaw = sessionStorage.getItem(
          STEBBY_COMPLEX_REGISTRATION_INPUT_KEY,
        );

        if (!reference || !registrationInputRaw) {
          console.error('❌ Missing Stebby reference or complex payload');
          clearStebbySession();
          setErrorDialogMessage(GENERIC_ERROR_MSG);
          setErrorDialogOpen(true);
          setStepIndex(-1);
          return;
        }

        const tokenResult = await getStebby(
          `/identify/token/${encodeURIComponent(reference)}`,
        );

        if (!stebbyResponse.ok || !tokenResult?.token) {
          console.error('❌ Failed to get Stebby token:', tokenResult);
          clearStebbySession();
          setErrorDialogMessage(GENERIC_ERROR_MSG);
          setErrorDialogOpen(true);
          setStepIndex(-1);
          return;
        }

        const stored = JSON.parse(registrationInputRaw);

        const payloadWithStebbyToken = {
          ...stored.payload,
          PaymentType: BACKEND_PAYMENT_TYPES.Stebby,
          paymentType: BACKEND_PAYMENT_TYPES.Stebby,
          StebbyToken: tokenResult.token,
          StebbyReference: reference,
        };

        clearStebbySession();

        await submitRegistrationByWorkflow({
          payload: payloadWithStebbyToken,
          formData: stored.formData,
          trackingData: stored.trackingData,
          exponeaPayload: stored.exponeaPayload,
        });
      } catch (err) {
        console.error('❌ Failed to complete complex Stebby flow:', err);
        clearStebbySession();
        setErrorDialogMessage(GENERIC_ERROR_MSG);
        setErrorDialogOpen(true);
        setStepIndex(-1);
      }
    };

    completeStebbyFlow();
  }, [
    clearStebbySession,
    getStebby,
    isStebbySuccessRoute,
    stebbyResponse.ok,
    submitRegistrationByWorkflow,
  ]);

  useEffect(() => {
    const unblock = history.block(() => {
      const nextPath = window.location.pathname;

      if (
        nextPath === '/kompleksines-paslaugos' &&
        stepIndex > 0 &&
        !isSuccessRoute &&
        !isStebbySuccessRoute
      ) {
        setStepIndex((prev) => prev - 1);
        return false;
      }

      return true;
    });

    return () => unblock();
  }, [history, isStebbySuccessRoute, isSuccessRoute, stepIndex]);

  const steps = useMemo(
    () => [
      {
        title: stepTitles[0],
        content: (
          <ComplexPriceStep prices={complex?.prices} goNext={nextStep} />
        ),
      },
      {
        title: stepTitles[1],
        content: (
          <ComplexPatientInfoStep
            goPrev={prevStep}
            paymentType={paymentType}
            onPaymentTypeChange={setPaymentType}
            isSubmitting={formSubmitting || isStebbyLoading}
            goNext={showPaymentOptions}
          />
        ),
      },
      {
        title: stepTitles[2],
        content: <SuccessStep />,
      },
    ],
    [
      complex?.prices,
      formSubmitting,
      isStebbyLoading,
      nextStep,
      paymentType,
      prevStep,
      showPaymentOptions,
      stepTitles,
    ],
  );

  const isCompleted = stepIndex === steps.length;
  const isError = stepIndex === -1;

  const currentStepEl =
    !isCompleted && !isError && steps[stepIndex]?.content
      ? React.cloneElement(steps[stepIndex].content, {
          stepIndex,
          stepNumber: stepIndex + 1,
          totalSteps: steps.length,
          isActive: true,
          isCompleted,
          setStepIndex,
        })
      : null;

  const isPaymentCallbackRoute =
    isSuccessRoute ||
    isErrorRoute ||
    isCancelRoute ||
    isStebbySuccessRoute ||
    isStebbyCancelRoute;

  if (loading && !complex && !isPaymentCallbackRoute) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg font-medium">Kraunama...</div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[1440px] mx-auto h-full p-4 font-primary">
      <div className="mb-4">
        <Stepper
          steps={stepTitles}
          currentStep={
            isCompleted || isSuccessRoute || isErrorRoute
              ? steps.length + 1
              : stepIndex + 1
          }
        />
      </div>

      <div className="pb-4 min-h-40">
        {isStebbySuccessRoute ? (
          <div className="flex items-center justify-center min-h-40">
            <div className="text-lg font-medium">
              Tvirtinamas Stebby mokėjimas...
            </div>
          </div>
        ) : isCompleted || isSuccessRoute ? (
          <SuccessStepComplex />
        ) : isErrorRoute || isCancelRoute || isStebbyCancelRoute || isError ? (
          <ErrorStep />
        ) : (
          currentStepEl
        )}
      </div>

      <RegistrationErrorDialog
        open={errorDialogOpen}
        onClose={() => {
          setErrorDialogOpen(false);
          history.push('/kompleksines-paslaugos');
        }}
        message={errorDialogMessage}
      />
    </div>
  );
};

export default ComplexCartPage;
